<template>
  <div class="tw-relative tw-justify-center md:tw-sticky md:tw-top-0">
    <PropertiesSearchFilterModal
      :bathroom-count="parseInt(selectedBathrooms, 10)"
      :current-max-price="selectedMaxPrice"
      :current-min-price="minCheapestRoomPrice"
      :current-enquired-properties="currentEnquiredProperties"
      :room-count="parseInt(selectedBedrooms, 10)"
      :show="showModal"
      :use-exact-room-counts="useExactRoomCounts"
      :enable-student-enquiries-cta="enableStudentEnquiriesCta"
      @close="handleModalClose"
      @set-max-price="handleMaxPriceChange"
      @set-current-bedrooms="handleCurrentBedroomsChange"
      @set-current-bathrooms="handleCurrentBathroomsChange"
    />
    <ExpandedPropertiesSearchFilterModal
      :show="showFilterModal"
      :current-university="currentUniversity"
      :current-area="currentArea"
      :universities="shortlistModeActive ? [] : allUniversities"
      :current-property-type="currentType"
      :current-expanded-properties="currentExpandedProperties"
      :current-enquired-properties="currentEnquiredProperties"
      :areas="shortlistModeActive ? [] : areas"
      :enable-property-type-filters="enablePropertyTypeFilters"
      :enable-private-halls-filter="enablePrivateHallsFilter"
      :enable-spare-room-functionality="enableSpareRoomFunctionality"
      :enable-student-enquiries-cta="enableStudentEnquiriesCta"
      :has-enquired-properties="hasEnquiredProperties"
      @close="handleFilterModalClose"
      @set-property-type="handlePropertyTypeChange"
      @set-university="handleUniversitySelection"
      @set-area="handleAreaSelection"
      @set-enquired-properties="handleEnquiredPropertiesChange"
    />

    <MobileSearchStackedFiltersModal
      :show="showMobileModal"
      :current-area="currentArea"
      :areas="shortlistModeActive ? [] : areas"
      :current-university="currentUniversity"
      :current-bedrooms="currentBedrooms"
      :current-bathrooms="currentBathrooms"
      :current-type="currentTypeAsString"
      :current-enquired-properties="currentEnquiredProperties"
      :universities="shortlistModeActive ? [] : allUniversities"
      :enable-private-halls-filter="enablePrivateHallsFilter"
      :enable-spare-room-functionality="enableSpareRoomFunctionality"
      :enable-local-filter-counts="enableLocalFilterCounts"
      :enable-student-enquiries-cta="enableStudentEnquiriesCta"
      :use-exact-room-counts="useExactRoomCounts"
      :has-enquired-properties="hasEnquiredProperties"
      @close="handleMobileModalClose"
      @set-min-price="handleMinPriceChange"
      @set-max-price="handleMaxPriceChange"
      @set-current-bedrooms="handleCurrentBedroomsChange"
      @set-current-bathrooms="handleCurrentBathroomsChange"
      @set-property-type="handlePropertyTypeChange"
      @set-area="handleAreaSelection"
      @set-university="handleUniversitySelection"
      @set-enquired-properties="handleEnquiredPropertiesChange"
    />

    <div class="contained tw-w-full tw-bg-white tw-py-3 lg:tw-hidden">
      <slot name="shortlist-list-selector-mobile" />
      <div class="tw-flex tw-items-center tw-justify-between">
        <div>
          <div class="tw-flex tw-items-center tw-space-x-1">
            <button
              v-if="mapStore.mapIsOpen && canShowMapView"
              type="button"
              class="tw-rounded-[25px] tw-border tw-p-[13px] hover:tw-bg-gray-100"
              @click="$emit('toggle-map-is-open')"
            >
              <img
                :src="cdnAsset('svg/listings/uh-cross.svg')"
                alt="Close"
                class="tw-h-[24px] tw-w-[24px]"
              />
            </button>
            <div>
              <div class="tw-flex tw-gap-1">
                <img
                  v-if="!shortlistModeActive"
                  :src="cdnAsset('svg/listings/location-pin-grey.svg')"
                  alt="Location Pin"
                  class="tw-h-[22px] tw-w-[22px]"
                />
                <span
                  v-if="shortlistModeActive"
                  class="tw-text-md tw-text-trueGray-700 tw-font-semibold"
                >
                  My Shortlist
                </span>
                <span
                  v-if="currentCity && !shortlistModeActive"
                  class="tw-text-md tw-text-trueGray-700 tw-font-semibold"
                >
                  {{
                    currentUniversity?.name ||
                    currentArea?.name ||
                    currentCity.name
                  }}
                </span>
              </div>
              <div
                class="tw-text-trueGray-600 tw-mt-[8px] tw-pl-[2px] tw-text-sm"
              >
                <span>
                  <strong>{{ propertyCount }}</strong>
                  {{ propertyCount === 1 ? 'result' : 'results' }}</span
                >
                <span v-if="currentCity && !shortlistModeActive"
                  >in {{ currentCity.name }}</span
                >
              </div>
            </div>
          </div>
        </div>
        <div class="tw-flex tw-items-center tw-gap-2">
          <button
            v-if="
              shortlistModeActive &&
              propertyCount > 0 &&
              !mapStore.mapIsOpen &&
              canShowMapView
            "
            type="button"
            class="tw-rounded-[25px] tw-border tw-p-[13px] hover:tw-bg-gray-100 md:tw-hidden"
            @click="openMapAndScroll()"
          >
            <img
              :src="cdnAsset('svg/uh-view-map-icon.svg')"
              alt="Map View"
              class="tw-h-[24px] tw-w-[24px]"
            />
          </button>
          <button
            v-if="!mapStore.mapIsOpen && canShowMapView && !canShowGridSplitMaps"
            type="button"
            class="tw-hidden tw-rounded-[25px] tw-border tw-p-[13px] hover:tw-bg-gray-100 md:tw-block"
            @click="openMapAndScroll()"
          >
            <img
              :src="cdnAsset('svg/uh-view-map-icon.svg')"
              alt="Map View"
              class="tw-h-[24px] tw-w-[24px]"
            />
          </button>
          <button
            type="button"
            :class="[
              'tw-rounded-full tw-border tw-p-3 hover:tw-bg-gray-100',
              { 'filter-button-with-count': appliedFiltersCount > 0 },
            ]"
            :data-filter-count="appliedFiltersCount"
            data-testid="mobile-more-filters"
            @click="handleMobileModalOpen"
          >
            <img
              :src="cdnAsset('svg/listings/sliders.svg')"
              alt="Price icon"
              class="tw-h-6 tw-w-6 lg:tw-mr-2"
            />
          </button>
        </div>
      </div>
    </div>

    <div
      id="search-element"
      class="container-fluid contained tw-mt-3 tw-hidden tw-h-20 tw-flex-col tw-items-center tw-gap-2 tw-bg-white lg:tw-mb-0 lg:tw-flex lg:tw-flex-row lg:tw-justify-start lg:tw-gap-[16px] lg:tw-bg-[#F9FAFB] lg:tw-px-0"
      :class="[
        enablePropertyTypeFilters
          ? 'lg:tw-mt-0 lg:tw-h-[100px] lg:tw-bg-[#FFFFFF]'
          : 'lg:tw-mt-4 lg:tw-h-24',
        'tw-max-w-screen-xl',
      ]"
    >
      <!-- Close Map Button / Back to results (Grid/Split full-page map) -->
      <button
        v-if="mapStore.mapIsOpen && canShowMapView"
        type="button"
        data-testid="marketplace-map-back-to-results"
        :class="[
          'tw-border-coolGray-300 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-2 tw-bg-white tw-px-4',
          desktopSearchInputHeightClasses,
          desktopFilterAccentBorderClasses,
        ]"
        @click="$emit('toggle-map-is-open')"
      >
        <img
          :src="cdnAsset('svg/arrow-icon-blue.svg')"
          alt="Back"
          class="tw-h-[24px] tw-w-[24px]"
        />
        <span class="tw-text-xs tw-font-semibold lg:tw-text-sm">{{
          canShowGridSplitMaps ? 'Back to results' : 'List view'
        }}</span>
      </button>
      <!-- location input filter -->
      <a
        v-if="shortlistModeActive"
        :href="shortlistReturnUrl"
        class="tw-font-inter tw-flex tw-h-[50px] tw-items-center tw-justify-center tw-gap-[8px] tw-rounded-[30px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-[24px] tw-py-[8px] tw-text-center tw-text-[14px] tw-font-bold tw-leading-[21px] tw-text-[#3B4143] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
        :class="
          enablePropertyTypeFilters
            ? 'hover:tw-border-[#0082B8] hover:tw-shadow-[inset_0_0_0_1px_#0082B8] focus:tw-border-[#0082B8] focus:tw-shadow-[inset_0_0_0_1px_#0082B8]'
            : 'hover:tw-border-[#00A2E1] hover:tw-shadow-[inset_0_0_0_1px_#00A2E1] focus:tw-border-[#00A2E1] focus:tw-shadow-[inset_0_0_0_1px_#00A2E1]'
        "
      >
        <svg
          aria-hidden="true"
          focusable="false"
          width="18.001"
          height="15.001"
          viewBox="0 0 18 15"
          class="tw-shrink-0"
        >
          <use href="#shortlist-back-arrow" />
        </svg>
        <span>Back</span>
      </a>
      <div
        v-else
        ref="searchLocationInputFilter"
        :class="[
          'tw-border-coolGray-300 tw-flex tw-h-10 tw-flex-row tw-items-center tw-rounded-full tw-border-2 tw-bg-white lg:tw-px-2.5',
          desktopSearchInputHeightClasses,
          mapStore.mapIsOpen
            ? 'tw-w-full lg:tw-flex-1'
            : 'lg:tw-min-w-0 tw-w-full lg:tw-flex-1',
          desktopFilterAccentBorderClasses,
        ]"
        @focusout="handleUniversityHide($event)"
        @focusin="handleUniversityFocus"
      >
        <img
          :src="cdnAsset('svg/listings/location-pin.svg')"
          alt="Location Pin"
          class="tw-ml-2 tw-h-[24px] tw-w-[24px]"
        />
        <input
          :value="currentUniversity ? currentUniversity.name : ''"
          placeholder="All Universities"
          type="text"
          readonly
          :class="{
            'focus:tw-border-primary-500 focus:tw-ring-primary-500 focus:tw-duration-100':
              shouldShowAreaDropdown,
          }"
          class="tw-mr-2 tw-grow-[0.8] tw-border-none tw-py-4 tw-pb-1 tw-pt-1.5 tw-align-middle tw-text-sm tw-font-normal tw-text-[#5C6A75] tw-caret-transparent tw-outline-none tw-ring-0 tw-ring-offset-transparent md:tw-font-semibold"
          @click="shouldShowAreaDropdown = true"
        />
        <div
          :style="computedsearchFilterWidthStyle"
          :class="{
            'tw-block': shouldShowAreaDropdown,
            'tw-hidden': !shouldShowAreaDropdown,
          }"
          class="search-filter-select-refresh tw-absolute tw-z-40"
        >
          <div
            class="search-filter-select-refresh-inner hide-scrollbar bg-white tw-absolute tw-top-[30px] tw-ml-[-12px] tw-max-h-[50vh] tw-w-full tw-overflow-y-auto tw-rounded-[16px] tw-border tw-border-[#E5E7EB] tw-drop-shadow-md"
          >
            <div
              v-for="(university, index) in allUniversities"
              :key="`uni-${index}`"
              :class="[
                'search-result-item tw-flex tw-cursor-pointer tw-flex-row tw-gap-3 tw-border-b-[1px] tw-border-[#E5E7EB] tw-px-[24px] tw-py-[16px] tw-text-primary-grey first:tw-rounded-t-[16px] last:tw-rounded-b-[16px] hover:tw-text-white',
                desktopFilterAccentDropdownClasses,
                {
                  'tw-border-b-[1px] tw-border-[#E5E7EB]':
                    allUniversities.length > 0,
                },
              ]"
              @mousedown="handleUniversitySelection(university)"
            >
              <div class="tw-flex tw-flex-row tw-items-center tw-gap-2">
                <span
                  class="bank-icon tw-ml-[8px] tw-h-[24px] tw-w-[20px]"
                ></span>
                <span class="tw-text-[16px] tw-leading-[24px]">{{
                  university.name
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Mobile filters -->
      <div
        class="tw-flex tw-w-full tw-flex-row tw-justify-between tw-px-1 hover:tw-cursor-pointer lg:tw-hidden"
      >
        <div
          class="tw-flex tw-flex-row tw-content-center tw-items-center tw-gap-2"
          @click="handleMobileModalOpen"
        >
          <img
            :src="cdnAsset('svg/new-bed-icon.svg')"
            alt="Bed icon"
            class="tw-h-6 tw-w-6 lg:tw-mr-2"
          />
          <span class="tw-text-sm tw-font-normal xl:tw-text-sm">{{
            displayFilter(selectedBedrooms, 'Any')
          }}</span>
        </div>

        <div
          class="tw-flex tw-flex-row tw-content-center tw-items-center tw-gap-2 hover:tw-cursor-pointer"
          @click="handleMobileModalOpen"
        >
          <img
            :src="cdnAsset('svg/new-coin-icon.svg')"
            alt="Price icon"
            class="tw-h-6 tw-w-6 lg:tw-mr-2"
          />
          <span class="tw-text-sm tw-font-normal xl:tw-text-sm"
            >{{ displayPrice === 'Any' ? '' : 'Max' }} {{ displayPrice }}
            {{ displayPrice === 'Any' ? '' : 'pppw' }}</span
          >
        </div>

        <div
          class="tw-flex tw-flex-row tw-content-center tw-items-center hover:tw-cursor-pointer md:tw-gap-1"
          @click="handleMobileModalOpen"
        >
          <img
            :src="cdnAsset('svg/listings/sliders.svg')"
            alt="Price icon"
            class="tw-h-6 tw-w-6 lg:tw-mr-2"
          />

          <img
            :src="cdnAsset('svg/listings/caret-right.svg')"
            alt="Price icon"
            class="tw-h-3 tw-w-3 lg:tw-mr-2"
          />
        </div>
      </div>
      <slot name="shortlist-list-selector-desktop" />
      <!-- Desktop Filters -->
      <PropertyTypeFiltersTrigger
        v-if="enablePropertyTypeFilters"
        :expanded="propertyTypeFiltersExpanded"
        class="tw-hidden tw-shrink-0 lg:tw-ml-auto lg:tw-flex"
        @toggle="propertyTypeFiltersExpanded = !propertyTypeFiltersExpanded"
      />
      <div
        class="tw-hidden tw-flex-row tw-gap-[16px] lg:tw-flex"
      >
        <div
          :class="[
            'tw-border-coolGray-300 tw-flex tw-w-[319px] tw-cursor-pointer tw-flex-row tw-items-center tw-justify-around tw-rounded-full tw-border-2 tw-bg-white tw-px-2.5',
            desktopSearchInputHeightClasses,
            desktopFilterAccentBorderClasses,
          ]"
          dusk="desktop-property-filters"
          data-testid="desktop-property-filters"
          @click="handleModalOpen"
        >
          <div
            class="tw-flex tw-flex-col tw-content-center tw-items-center lg:tw-flex-row"
          >
            <img
              :src="cdnAsset('svg/uh-bed-icon.svg')"
              alt="Bed icon"
              class="tw-h-4 tw-w-4 lg:tw-mr-2"
            />
            <span class="tw-text-xs tw-font-semibold xl:tw-text-sm">{{
              displayFilter(selectedBedrooms, 'Any')
            }}</span>
          </div>

          <div class="tw-h-6 tw-w-px tw-bg-gray-300" />

          <div
            class="tw-flex tw-flex-col tw-content-center tw-items-center lg:tw-flex-row"
          >
            <img
              :src="cdnAsset('svg/listings/shower.svg')"
              alt="Shower and Bathroom icon"
              class="tw-h-4 tw-w-4 lg:tw-mr-2"
            />
            <span class="tw-text-xs tw-font-semibold xl:tw-text-sm">{{
              useExactRoomCounts
                ? displayFilter(selectedBathrooms, 'Any')
                : displayFilter(selectedBathrooms, 'Any', '+')
            }}</span>
          </div>

          <div class="tw-h-6 tw-w-px tw-bg-gray-300" />

          <div class="tw-flex tw-flex-row tw-content-center tw-items-center">
            <img
              :src="cdnAsset('svg/listings/uh-coins.svg')"
              alt="Price icon"
              class="tw-h-4 tw-w-4 lg:tw-mr-2"
            />
            <span class="tw-text-xs tw-font-semibold xl:tw-text-sm">{{
              displayPrice
            }}</span>
          </div>
        </div>
        <div
          :class="[
            'tw-border-coolGray-300 tw-flex tw-w-[165px] tw-cursor-pointer tw-flex-row tw-items-center tw-justify-center tw-rounded-full tw-border tw-bg-white',
            desktopSearchInputHeightClasses,
            desktopFilterAccentBorderClasses,
          ]"
          dusk="desktop-more-filters"
          data-testid="desktop-more-filters"
          @click="handleFilterModalOpen"
        >
          <img
            :src="cdnAsset('svg/listings/filters.svg')"
            alt="Price icon"
            class="tw-ml-1 tw-mr-1 tw-h-4 tw-w-4 tw-items-center sm:tw-ml-2 md:tw-ml-1 lg:tw-ml-0 lg:tw-mr-1"
          />
          <span
            class="tw-cursor-pointer tw-text-xs tw-font-semibold md:tw-w-[3rem] lg:tw-w-auto lg:tw-text-sm"
            >More filters</span
          >
        </div>
      </div>
    </div>

    <PropertyTypeFiltersExtension
      v-if="enablePropertyTypeFilters"
      :break-out-of-container="false"
      class="tw-hidden lg:tw-block"
      :active="currentType"
      :expanded="propertyTypeFiltersExpanded"
      :enable-private-halls-filter="enablePrivateHallsFilter"
      :enable-spare-room-functionality="enableSpareRoomFunctionality"
      @update:expanded="propertyTypeFiltersExpanded = $event"
      @change="handleInlinePropertyTypeChange"
    />
  </div>
</template>

<script>
import { nextTick } from 'vue';
import PropertiesSearchFilterModal from '@components/PropertiesSearchFilterModal.vue';
import ExpandedPropertiesSearchFilterModal from '@components/ExpandedPropertiesSearchFilterModal.vue';
import MobileSearchStackedFiltersModal from '@components/MobileSearchStackedFiltersModal.vue';
import PropertyTypeFiltersExtension from '@components/Property/MobileSearchFilters/PropertyTypeFiltersExtension.vue';
import PropertyTypeFiltersTrigger from '@components/Property/MobileSearchFilters/PropertyTypeFiltersTrigger.vue';
import { useMapStore } from '@js/stores/map/mapStore';
import { getCurrentPath } from '@js/utilities';

export default {
  components: {
    PropertiesSearchFilterModal,
    ExpandedPropertiesSearchFilterModal,
    MobileSearchStackedFiltersModal,
    PropertyTypeFiltersExtension,
    PropertyTypeFiltersTrigger,
  },
  props: {
    shortlistModeActive: {
      default: false,
      type: Boolean,
    },
    enableMarketplaceGridSplitMaps: {
      default: false,
      type: Boolean,
    },
    areas: {
      default: function () {
        return [];
      },
      type: Array,
    },
    cities: {
      default: function () {
        return [];
      },
      type: Array,
    },
    currentCitySlug: {
      default: null,
      type: String,
    },
    currentLandlord: {
      default: null,
      type: [Object, String],
    },
    currentUniversitySlug: {
      default: null,
      type: String,
    },
    currentAreaSlug: {
      default: null,
      type: String,
    },
    currentBedrooms: {
      default: null,
      type: [Number, String],
    },
    currentBathrooms: {
      default: null,
      type: [Number, String],
    },
    currentType: {
      default: null,
      type: [String, Array],
    },
    currentMaxPrice: {
      default: null,
      type: [Number, String],
    },
    currentMinPrice: {
      default: null,
      type: [Number, String],
    },
    currentExpandedProperties: {
      default: false,
      type: Boolean,
    },
    currentEnquiredProperties: {
      type: Boolean,
      default: false,
    },
    maxCheapestRoomPrice: {
      default: null,
      type: [Number, String],
    },
    maxPriceSelected: {
      default: false,
      type: Boolean,
    },
    minCheapestRoomPrice: {
      default: null,
      type: [Number, String],
    },
    landlordQuery: {
      default: null,
      type: [Number, String],
    },
    propertyCount: {
      default: 0,
      type: Number,
    },
    reloading: {
      default: false,
      type: Boolean,
    },
    rooms: {
      default: function () {
        return [];
      },
      type: Array,
    },
    types: {
      default: function () {
        return [];
      },
      type: Array,
    },
    universities: {
      default: function () {
        return [];
      },
      type: Array,
    },
    enablePrivateHallsFilter: {
      default: false,
      type: Boolean,
    },
    enableSpareRoomFunctionality: {
      default: false,
      type: Boolean,
    },
    enablePropertyTypeFilters: {
      default: false,
      type: Boolean,
    },
    enableLocalFilterCounts: {
      default: false,
      type: Boolean,
    },
    enableStudentEnquiriesCta: {
      type: Boolean,
      default: false,
    },
    useExactRoomCounts: {
      type: Boolean,
      default: false,
    },
    hasEnquiredProperties: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const mapStore = useMapStore();
    return {
      mapStore,
      showModal: false,
      showFilterModal: false,
      showMobileModal: false,
      shouldShowAreaDropdown: false,
      universityBlurTimer: null,
      selectedBedrooms: this.currentBedrooms ?? 0,
      selectedBathrooms: this.currentBathrooms ?? 0,
      selectedMaxPrice: this.currentMaxPrice
        ? this.currentMaxPrice
        : this.maxCheapestRoomPrice,
      setMaxPrice: this.currentMaxPrice
        ? this.currentMaxPrice
        : this.maxCheapestRoomPrice,
      searchFilterWidth: 0,
      propertyTypeFiltersExpanded: true,
    };
  },
  computed: {
    shortlistReturnUrl() {
      const url = new URL(window.location.href);
      url.searchParams.delete('tab');
      url.searchParams.delete('shortlistHash');

      return `${url.pathname}${url.search}${url.hash}`;
    },
    appliedFiltersCount() {
      let count = 0;

      // Price filter (min or max price set)
      if (this.currentMinPrice || this.currentMaxPrice) count++;

      // Bedrooms filter
      if (this.currentBedrooms) count++;

      // Bathrooms filter
      if (this.currentBathrooms) count++;

      // Property Type filter
      if (this.currentType) count++;

      // Area filter
      if (this.currentAreaSlug) count++;

      // University filter
      if (this.currentUniversitySlug) count++;

      return count;
    },
    currentTypeAsString() {
      if (!this.currentType) return null;
      if (Array.isArray(this.currentType)) {
        return this.currentType.join(',');
      }
      return this.currentType;
    },
    desktopFilterAccentBorderClasses() {
      return this.enablePropertyTypeFilters
        ? 'hover:tw-border-2 hover:tw-border-[#0082B8] focus:tw-border-2 focus:tw-border-[#0082B8]'
        : 'hover:tw-border-2 hover:tw-border-[#00A2E1] focus:tw-border-2 focus:tw-border-[#00A2E1]';
    },
    desktopSearchInputHeightClasses() {
      return this.enablePropertyTypeFilters ? 'lg:tw-h-[52px]' : 'lg:tw-h-12';
    },
    desktopFilterAccentDropdownClasses() {
      return this.enablePropertyTypeFilters
        ? 'hover:tw-bg-[#0082B8] focus:tw-bg-[#0082B8]'
        : 'hover:tw-bg-[#00A2E1] focus:tw-bg-[#00A2E1]';
    },
    displayPrice() {
      const maxPrice = this.currentMaxPrice;

      if (maxPrice && typeof maxPrice === 'string') {
        const intValue = parseInt(maxPrice, 10);
        return isNaN(intValue) ? 'Any' : `£${intValue}`;
      }

      if (maxPrice && typeof maxPrice === 'number') {
        return `£${maxPrice}`;
      }

      return 'Any';
    },
    computedsearchFilterWidthStyle() {
      return { width: `${this.searchFilterWidth}px` };
    },
    currentArea() {
      let scope = this;
      return scope.currentAreaSlug
        ? this.areas.filter(function (area) {
            return area.slug === scope.currentAreaSlug;
          })[0]
        : null;
    },
    currentCity() {
      let scope = this;
      return scope.currentCitySlug
        ? this.cities.filter(function (city) {
            return city.id === scope.currentCitySlug;
          })[0]
        : null;
    },
    currentMaxPriceString() {
      return this.maxCheapestRoomPrice
        ? 'Max: &pound;' + this.maxCheapestRoomPrice
        : false;
    },
    currentMaxPriceIsInvalid() {
      return this.selectedMaxPrice
        ? /\d/.test(this.selectedMaxPrice) === false
        : false;
    },
    currentMaxCheapestRoomPrice() {
      return parseInt(this.maxCheapestRoomPrice, 10);
    },
    currentMinCheapestRoomPrice() {
      return parseInt(this.minCheapestRoomPrice, 10);
    },
    currentUniversity() {
      let scope = this;
      return scope.currentUniversitySlug
        ? this.universities.filter(function (university) {
            return university.slug === scope.currentUniversitySlug;
          })[0]
        : null;
    },
    allUniversities() {
      return this.universities || [];
    },

    mapIsOpen() {
      return this.mapStore.mapIsOpen;
    },
    canShowMapView() {
      const currentPath = this.getCurrentPath();
      return currentPath && currentPath !== '/student-accommodation';
    },
    canShowGridSplitMaps() {
      return Boolean(
        this.canShowMapView && this.enableMarketplaceGridSplitMaps
      );
    },
  },
  watch: {
    propertyTypeFiltersExpanded(isExpanded) {
      this.syncDesktopPropertyFiltersState(isExpanded);
    },
    currentBedrooms(newVal) {
      this.selectedBedrooms =
        newVal === null || newVal === undefined || newVal === '' ? 0 : newVal;
    },
    currentBathrooms(newVal) {
      this.selectedBathrooms =
        newVal === null || newVal === undefined || newVal === '' ? 0 : newVal;
    },
    'mapStore.selectedUniversity': {
      handler(newUniversity) {
        // When university is selected in the map, update the filters
        if (newUniversity) {
          // Find the matching university in the universities array by ID
          const foundUniversity = this.universities.find(
            (u) => String(u.id) === String(newUniversity.id)
          );

          if (foundUniversity && foundUniversity.slug) {
            // Only emit if it's different from current selection to avoid loops
            if (this.currentUniversitySlug !== foundUniversity.slug) {
              this.$emit('set-current-university-slug', foundUniversity.slug);
            }
          }
        } else {
          // University was cleared in the map, clear it in filters too
          if (this.currentUniversitySlug) {
            this.$emit('set-current-university-slug', null);
          }
        }
      },
    },
  },
  mounted() {
    this.updateWidth();
    window.addEventListener('resize', this.updateWidth);
    this.syncDesktopPropertyFiltersState(this.propertyTypeFiltersExpanded);
    // Initialize map store with current university if one is selected
    if (this.currentUniversity) {
      this.mapStore.setSelectedUniversity(this.currentUniversity);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWidth);
  },
  methods: {
    getCurrentPath,
    syncDesktopPropertyFiltersState(isExpanded) {
      // Only Grid side-map uses this to resize the sticky map. Skip when the
      // flag is off so classic list/map matches pre-grid behaviour.
      if (!this.canShowGridSplitMaps) {
        return;
      }

      if (!this.enablePropertyTypeFilters) {
        this.$emit('desktop-property-filters-close');
        return;
      }

      this.$emit(
        isExpanded
          ? 'desktop-property-filters-open'
          : 'desktop-property-filters-close'
      );
    },
    updateWidth() {
      nextTick(() => {
        const element = this.$refs.searchLocationInputFilter;

        if (element) {
          this.searchFilterWidth = element.offsetWidth;
        }
      });
    },
    displayFilter(value, defaultText = 'Any', symbol = '') {
      if (!value) {
        return defaultText;
      }

      if (typeof value === 'string') {
        const intValue = parseInt(value, 10);
        return isNaN(intValue) ? defaultText : intValue + symbol;
      }
      return typeof value === 'number' ? value + symbol : defaultText;
    },
    handleUniversitySelection(university) {
      if (this.shortlistModeActive) return;

      this.shouldShowAreaDropdown = false;
      this.$emit('set-current-university-slug', university?.slug || null);
      // Set university in map store for travel times
      if (university && university.id) {
        this.mapStore.setSelectedUniversity(university);
      } else {
        this.mapStore.setSelectedUniversity(null);
      }
    },
    handleUniversityHide(event) {
      if (this.universityBlurTimer) {
        clearTimeout(this.universityBlurTimer);
      }
      this.universityBlurTimer = setTimeout(() => {
        // Ensure the dropdown is not open before hiding
        if (
          !this.$refs.searchLocationInputFilter.contains(event.relatedTarget)
        ) {
          this.shouldShowAreaDropdown = false;
        }
      }, 150);
    },
    handleUniversityFocus() {
      if (this.universityBlurTimer) {
        clearTimeout(this.universityBlurTimer);
      }
    },
    handleMinPriceChange(value) {
      this.selectedMinPrice = parseInt(value, 10);
      this.$emit('set-current-min-price', value);
      this.$emit('set-min-price-selected', true);
    },
    handleMaxPriceChange(value) {
      let parsed = value;

      if (typeof parsed === 'string') {
        parsed = parseInt(parsed);
      }

      this.selectedMaxPrice = parsed;
      this.$emit('set-current-max-price', parsed);
      this.$emit('set-max-price-selected', true);
    },
    handleCurrentBedroomsChange(value) {
      this.selectedBedrooms = value;
      this.$emit('set-current-bedrooms', value);
    },
    handleCurrentBathroomsChange(value) {
      this.selectedBathrooms = value;
      this.$emit('set-current-bathrooms', value);
    },
    handleModalClose() {
      this.showModal = false;
      this.$emit('filter-modal-close');
    },
    handleModalOpen() {
      this.shouldShowAreaDropdown = false;
      this.showModal = true;
    },
    handleFilterModalOpen() {
      this.shouldShowAreaDropdown = false;
      this.showFilterModal = true;
    },
    handleFilterModalClose() {
      this.showFilterModal = false;
      this.$emit('filter-modal-close');
    },
    handleMobileModalOpen() {
      // prevent double scroll when modal is active
      document.body.classList.add('tw-overflow-hidden');

      this.shouldShowAreaDropdown = false;
      this.showMobileModal = true;
    },
    handleMobileModalClose() {
      this.showMobileModal = false;
      this.$emit('filter-modal-close');
    },
    handlePropertyTypeChange(propertyType) {
      this.$emit('set-current-type', propertyType);
    },
    handleInlinePropertyTypeChange(propertyType) {
      this.$emit('apply-inline-property-type-filter', propertyType);
    },
    handleAreaSelection(area) {
      if (this.shortlistModeActive) return;

      this.$emit('set-current-area-slug', area?.slug || null);

      this.triggerLocationUpdate(area);
    },
    triggerLocationUpdate(area) {
      const locationChangedEvent = new CustomEvent('locationChanged', {
        detail: {
          city: this.currentCitySlug,
          area: area?.slug,
        },
      });

      document.dispatchEvent(locationChangedEvent);
    },
    openMapAndScroll() {
      this.$emit('toggle-map-is-open');
      this.$nextTick(() => {
        window.scrollTo({ top: 0 });
      });
    },
    handleEnquiredPropertiesChange(checked) {
      this.$emit('set-enquired-properties', checked);
    },
  },
};
</script>

<style scoped>
@media (max-width: 1023px) {
  .search-filter-select-refresh,
  .search-filter-select-refresh-inner {
    max-width: calc(100vw - 32px);
  }
}

/* Hide scrollbar while keeping scrolling functionality */
.hide-scrollbar {
  /* For Webkit browsers (Chrome, Safari, Edge) */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.hide-scrollbar::-webkit-scrollbar {
  display: none; /* Webkit browsers */
}

/* Filter count badge */
.filter-button-with-count {
  position: relative;
  min-width: 55px;
  min-height: 55px;
}

.filter-button-with-count::after {
  content: attr(data-filter-count);
  position: absolute;
  top: 0;
  right: 0;
  background: #f48831;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
}
</style>
